import { useMemo } from 'react';
import { BotsActivityOverviewResponse } from '../../../shared/types/bots-activity/BotsActivityOverviewResponse';
import { BotsActivityContentResponse } from '../../../shared/types/bots-activity/BotsActivityContentResponse';
import { BotsActivityTimeseriesResponse } from '../../../shared/types/bots-activity/BotsActivityTimeseriesResponse';
import { BotsActivityStatusResponse } from '../../../shared/types/bots-activity/BotsActivityStatusResponse';
import { getBotColor, getPlatformDisplayName } from '../../../shared/constants/botsActivityColors';
import { classifyBotType } from './useBotsActivityFilters';

const FEATURED_PLATFORMS = ['ChatGPT', 'Perplexity', 'Claude', 'Google'] as const;
const FEATURED_CARDS_MAX = 4;

const aggregateBreakdowns = (overview: BotsActivityOverviewResponse | undefined) => {
  const platforms = overview?.platforms ?? [];
  const platformMap: Record<string, number> = {};
  const botMap: Record<string, number> = {};
  const typeMap: Record<string, number> = {};

  for (const row of platforms) {
    if (!row.platform || !row.botName) continue;
    const botType = classifyBotType(row.botName);
    const platformKey = getPlatformDisplayName(row.platform);
    platformMap[platformKey] = (platformMap[platformKey] ?? 0) + row.current;
    botMap[row.botName] = (botMap[row.botName] ?? 0) + row.current;
    typeMap[botType] = (typeMap[botType] ?? 0) + row.current;
  }

  const toSorted = (map: Record<string, number>) =>
    Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .map(([label, count]) => ({ label, count, color: getBotColor(label) }));

  return {
    byPlatform: toSorted(platformMap),
    byBot: toSorted(botMap),
    byBotType: Object.entries(typeMap)
      .sort((a, b) => b[1] - a[1])
      .map(([label, count]) => ({ label, count, color: getBotColor(label) })),
  };
};

const computeTrendPct = (current: number, previous: number): number | null => {
  if (previous > 0) return Math.round(((current - previous) / previous) * 100);
  if (current > 0) return null;
  return 0;
};

const aggregateFeaturedPlatforms = (
  overview: BotsActivityOverviewResponse | undefined,
  anyFilterActive: boolean
) => {
  const totals: Record<string, { current: number; previous: number }> = {};
  for (const row of overview?.platforms ?? []) {
    if (!row.platform) continue;
    const key = getPlatformDisplayName(row.platform);
    const existing = totals[key] ?? { current: 0, previous: 0 };
    totals[key] = {
      current: existing.current + row.current,
      previous: existing.previous + row.previous,
    };
  }

  // Normal view: pin the 4 reference platforms regardless of traffic — they
  // anchor what the user is here to compare against. Once any filter is on,
  // those fixed slots would all collapse to 0; swap them for the top platforms
  // remaining in the filtered dataset so the cards stay informative.
  const platforms = anyFilterActive
    ? Object.entries(totals)
        .sort((a, b) => b[1].current - a[1].current)
        .slice(0, FEATURED_CARDS_MAX)
        .map(([name]) => name)
    : (FEATURED_PLATFORMS as readonly string[]);

  return platforms.map((p) => {
    const t = totals[p] ?? { current: 0, previous: 0 };
    return {
      platform: p,
      current: t.current,
      previous: t.previous,
      trendPct: computeTrendPct(t.current, t.previous),
    };
  });
};

export const useBotsActivityAggregations = (
  filteredOverview: BotsActivityOverviewResponse | undefined,
  contentFolder: BotsActivityContentResponse | undefined,
  contentUrl: BotsActivityContentResponse | undefined,
  timeseries: BotsActivityTimeseriesResponse | undefined,
  status: BotsActivityStatusResponse | undefined,
  anyFilterActive: boolean = false
) => {
  const { byPlatform, byBot, byBotType } = useMemo(
    () => aggregateBreakdowns(filteredOverview),
    [filteredOverview]
  );

  const featuredPlatforms = useMemo(
    () => aggregateFeaturedPlatforms(filteredOverview, anyFilterActive),
    [filteredOverview, anyFilterActive]
  );

  const topUrls = useMemo(() => contentUrl?.content ?? [], [contentUrl]);
  const topFolders = useMemo(() => contentFolder?.content ?? [], [contentFolder]);
  const excludedFileVisits = contentFolder?.excludedFileVisits;

  const totals = useMemo(() => {
    const platforms = filteredOverview?.platforms ?? [];
    const totalVisits = platforms.reduce((s, p) => s + p.current, 0);
    const previousVisits = platforms.reduce((s, p) => s + p.previous, 0);
    const activeBots = new Set(
      platforms
        .filter((p) => p.current > 0)
        .map((p) => p.botName)
        .filter(Boolean)
    ).size;
    const previousActiveBots = new Set(
      platforms
        .filter((p) => p.previous > 0)
        .map((p) => p.botName)
        .filter(Boolean)
    ).size;

    const computeTrend = (current: number, previous: number): number | null => {
      if (previous > 0) return Math.round(((current - previous) / previous) * 100);
      return current > 0 ? null : 0;
    };

    return {
      totalVisits,
      previousVisits,
      activeBots,
      previousActiveBots,
      visitsTrend: computeTrend(totalVisits, previousVisits),
      activeBotsTrend: computeTrend(activeBots, previousActiveBots),
    };
  }, [filteredOverview]);

  const headlineParts = useMemo(() => {
    if (!filteredOverview || totals.totalVisits === 0) return null;
    const top = byPlatform[0];
    const topFolder = topFolders[0];
    if (!top) return null;
    const sharePct =
      totals.totalVisits > 0 ? Math.round((top.count / totals.totalVisits) * 100) : 0;
    return { platform: top.label, sharePct, folder: topFolder?.path ?? null };
  }, [filteredOverview, byPlatform, topFolders, totals.totalVisits]);

  const hasNoActivityData = useMemo(() => {
    const tsEmpty =
      !timeseries?.timeseries ||
      timeseries.timeseries.length === 0 ||
      timeseries.timeseries.every((p) => p.count === 0);
    return totals.totalVisits === 0 && tsEmpty;
  }, [timeseries, totals.totalVisits]);

  const failureRate = status?.failureRate ?? null;
  const previousFailureRate = status?.previousFailureRate ?? null;

  return {
    byPlatform,
    byBot,
    byBotType,
    featuredPlatforms,
    topUrls,
    topFolders,
    excludedFileVisits,
    totals,
    headlineParts,
    hasNoActivityData,
    failureRate,
    previousFailureRate,
  };
};
